import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";

const normalizeCode = (value) => {
  const only = String(value || "").replace(/\D/g, "").slice(0, 3);
  return only ? only.padStart(3, "0") : "";
};

const emptyEstForm = {
  id: null,
  codigo: "",
  nombre: "",
  direccion: "",
  activo: true,
};

const normalizeEstablecimientos = (establecimientos, empresa) => {
  if (Array.isArray(establecimientos) && establecimientos.length > 0) {
    return establecimientos
      .map((e) => ({
        id: e.id || null,
        codigo: normalizeCode(e.codigo),
        nombre: String(e.nombre || "").trim(),
        direccion: String(e.direccion || "").trim(),
        activo: typeof e.activo === "boolean" ? e.activo : true,
      }))
      .filter((e) => e.codigo)
      .map((e) => ({
        ...e,
        nombre: e.nombre || `ESTABLECIMIENTO ${e.codigo}`,
      }));
  }

  const codigo = normalizeCode(empresa?.numero_establecimiento || "");

  if (!codigo) return [];

  return [
    {
      id: null,
      codigo,
      nombre: `ESTABLECIMIENTO ${codigo}`,
      direccion: empresa?.direccion || "",
      activo: true,
    },
  ];
};

const EnterpriseForm = ({
  empresa,
  setEmpresa,
  handleChange,
  handleCheck,
  handleFile,
  fileLabel,
  openPreview,
  submit,
  logoRef,
  loading,
  saving,
  canSave,
  canDelete,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { dashboardTheme } = useDashboardTheme();

  const [estModalOpen, setEstModalOpen] = useState(false);
  const [estForm, setEstForm] = useState(emptyEstForm);
  const [estEditIndex, setEstEditIndex] = useState(null);
  const [estError, setEstError] = useState("");

  const establecimientos = useMemo(() => {
    return normalizeEstablecimientos(empresa.establecimientos, empresa);
  }, [empresa]);

  const disabled = !canSave || saving || loading;
  const canManageEstablecimientos = canSave && !saving && !loading;
  const canDeleteEstablecimientos = canDelete && canManageEstablecimientos;

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: dashboardTheme.card_background,
      color: dashboardTheme.text_color,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
      "&.Mui-focused": {
        color: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: dashboardTheme.muted_text_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.muted_text_color,
    },
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: dashboardTheme.text_color,
    borderColor: dashboardTheme.border_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      bgcolor: `${dashboardTheme.primary_color}12`,
      boxShadow: "none",
    },
  };

  const checkboxSx = {
    color: dashboardTheme.muted_text_color,
    "&.Mui-checked": {
      color: dashboardTheme.primary_color,
    },
  };

  const sectionSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: "12px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
  };

  const setEstablecimientos = (next) => {
    const normalized = normalizeEstablecimientos(next, empresa);

    setEmpresa((prev) => ({
      ...prev,
      establecimientos: normalized,
      numero_establecimiento: normalized.map((e) => e.codigo).join(", "),
    }));
  };

  const openCreateEst = () => {
    if (!canManageEstablecimientos) return;

    setEstEditIndex(null);
    setEstError("");
    setEstForm({
      ...emptyEstForm,
      direccion: empresa.direccion || "",
      activo: true,
    });
    setEstModalOpen(true);
  };

  const openEditEst = (item, index) => {
    if (!canManageEstablecimientos) return;

    setEstEditIndex(index);
    setEstError("");
    setEstForm({
      id: item.id || null,
      codigo: normalizeCode(item.codigo),
      nombre: item.nombre || "",
      direccion: item.direccion || "",
      activo: typeof item.activo === "boolean" ? item.activo : true,
    });
    setEstModalOpen(true);
  };

  const closeEstModal = () => {
    setEstModalOpen(false);
    setEstEditIndex(null);
    setEstError("");
    setEstForm(emptyEstForm);
  };

  const saveEstablecimiento = () => {
    const codigo = normalizeCode(estForm.codigo);
    const nombre = String(estForm.nombre || "").trim() || `ESTABLECIMIENTO ${codigo}`;
    const direccion = String(estForm.direccion || "").trim();
    const activo = !!estForm.activo;

    if (!codigo || codigo === "000") {
      setEstError("Ingrese un código de establecimiento válido.");
      return;
    }

    const exists = establecimientos.some((e, index) => {
      if (estEditIndex !== null && index === estEditIndex) return false;
      return normalizeCode(e.codigo) === codigo;
    });

    if (exists) {
      setEstError("Ya existe un establecimiento con ese código.");
      return;
    }

    const item = {
      id: estForm.id || null,
      codigo,
      nombre,
      direccion,
      activo,
    };

    if (estEditIndex !== null) {
      setEstablecimientos(establecimientos.map((e, index) => (index === estEditIndex ? item : e)));
    } else {
      setEstablecimientos([...establecimientos, item]);
    }

    closeEstModal();
  };

  const deleteEstablecimiento = (index) => {
    if (!canDeleteEstablecimientos) return;

    const next = establecimientos.filter((_, i) => i !== index);
    setEstablecimientos(next.length ? next : []);
  };

  const handleEstFormChange = (patch) => {
    setEstForm((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  return (
    <Box component="form" onSubmit={submit}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${dashboardTheme.border_color}`,
          bgcolor:
            dashboardTheme.theme_mode === "dark"
              ? dashboardTheme.card_background
              : `${dashboardTheme.primary_color}0F`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: { xs: 17, md: 18 },
              fontWeight: 600,
              color: dashboardTheme.text_color,
            }}
          >
            Configuraciones de la Empresa
          </Typography>

          {!canSave && (
            <Typography
              sx={{
                px: 1.2,
                py: 0.6,
                borderRadius: "999px",
                fontSize: 12,
                fontWeight: 600,
                color: dashboardTheme.muted_text_color,
                bgcolor:
                  dashboardTheme.theme_mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(17,24,39,0.06)",
              }}
            >
              Solo lectura
            </Typography>
          )}
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Stack
            spacing={1.2}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 240 }}
          >
            <CircularProgress size={30} sx={{ color: dashboardTheme.primary_color }} />
            <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
              Cargando configuración...
            </Typography>
          </Stack>
        ) : (
          <>
            <Paper elevation={0} sx={sectionSx}>
              <Grid container spacing={1.6}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="RUC"
                    name="ruc"
                    value={empresa.ruc || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Razón social"
                    name="razon_social"
                    value={empresa.razon_social || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nombre comercial"
                    name="nombre_comercial"
                    value={empresa.nombre_comercial || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ciudad"
                    name="ciudad"
                    value={empresa.ciudad || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Teléfonos"
                    name="telefonos"
                    value={empresa.telefonos || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="Email notificación"
                    name="email_notificacion"
                    value={empresa.email_notificacion || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Dirección"
                    name="direccion"
                    value={empresa.direccion || ""}
                    onChange={handleChange}
                    disabled={disabled}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <Stack spacing={1.5}>
                {canManageEstablecimientos && (
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={openCreateEst}
                      sx={primaryButtonSx}
                    >
                      Añadir establecimiento
                    </Button>
                  </Box>
                )}

                {establecimientos.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: `1px dashed ${dashboardTheme.border_color}`,
                      bgcolor:
                        dashboardTheme.theme_mode === "dark"
                          ? "rgba(255,255,255,0.03)"
                          : "#f9fafb",
                    }}
                  >
                    <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                      No hay establecimientos configurados.
                    </Typography>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    {establecimientos.map((e, index) => (
                      <Paper
                        key={`${e.codigo}-${index}`}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: `1px solid ${dashboardTheme.border_color}`,
                          bgcolor:
                            dashboardTheme.theme_mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "#ffffff",
                          opacity: e.activo ? 1 : 0.72,
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <StorefrontOutlinedIcon
                            sx={{
                              mt: 0.3,
                              color: dashboardTheme.primary_color,
                              flexShrink: 0,
                            }}
                          />

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: dashboardTheme.text_color,
                                lineHeight: 1.2,
                                wordBreak: "break-word",
                              }}
                            >
                              {e.codigo} - {e.nombre}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 13,
                                color: dashboardTheme.muted_text_color,
                                mt: 0.5,
                                lineHeight: 1.45,
                                wordBreak: "break-word",
                              }}
                            >
                              {e.direccion || "Sin dirección específica"}
                            </Typography>

                            <Typography
                              sx={{
                                display: "inline-flex",
                                mt: 1,
                                px: 1,
                                py: 0.3,
                                borderRadius: "999px",
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor: e.activo
                                  ? dashboardTheme.theme_mode === "dark"
                                    ? "rgba(34,197,94,0.14)"
                                    : "#dcfce7"
                                  : dashboardTheme.theme_mode === "dark"
                                  ? "rgba(255,255,255,0.08)"
                                  : "#eeeeee",
                                color: e.activo ? "#16a34a" : dashboardTheme.muted_text_color,
                              }}
                            >
                              {e.activo ? "Activo" : "Inactivo"}
                            </Typography>
                          </Box>

                          {(canManageEstablecimientos || canDeleteEstablecimientos) && (
                            <Stack direction="row" spacing={0.4}>
                              {canManageEstablecimientos && (
                                <Tooltip title="Editar establecimiento">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditEst(e, index)}
                                    sx={{
                                      color: dashboardTheme.text_color,
                                      "&:hover": {
                                        bgcolor: `${dashboardTheme.primary_color}18`,
                                      },
                                    }}
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canDeleteEstablecimientos && (
                                <Tooltip title="Eliminar establecimiento">
                                  <IconButton
                                    size="small"
                                    onClick={() => deleteEstablecimiento(index)}
                                    sx={{
                                      color: dashboardTheme.text_color,
                                      "&:hover": {
                                        bgcolor: "rgba(239,68,68,0.12)",
                                      },
                                    }}
                                  >
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <Grid container spacing={1.6} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" sx={fieldSx}>
                    <InputLabel>Obligado a llevar contabilidad</InputLabel>
                    <Select
                      label="Obligado a llevar contabilidad"
                      name="obligado_contabilidad"
                      value={empresa.obligado_contabilidad || "NO"}
                      onChange={handleChange}
                      disabled={disabled}
                    >
                      <MenuItem value="NO">NO</MenuItem>
                      <MenuItem value="SI">SI</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 0.3,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!empresa.contribuyente_especial}
                          onChange={handleCheck}
                          name="contribuyente_especial"
                          disabled={disabled}
                          sx={checkboxSx}
                        />
                      }
                      label="Contribuyente especial"
                      sx={{ color: dashboardTheme.text_color }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!empresa.gran_contribuyente}
                          onChange={handleCheck}
                          name="gran_contribuyente"
                          disabled={disabled}
                          sx={checkboxSx}
                        />
                      }
                      label="Gran contribuyente"
                      sx={{ color: dashboardTheme.text_color }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!empresa.agente_retencion}
                          onChange={handleCheck}
                          name="agente_retencion"
                          disabled={disabled}
                          sx={checkboxSx}
                        />
                      }
                      label="Agente de retención"
                      sx={{ color: dashboardTheme.text_color }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!empresa.regimen_general || empresa.regimen_tipo === "GENERAL"}
                          onChange={handleCheck}
                          name="regimen_general"
                          disabled={disabled}
                          sx={checkboxSx}
                        />
                      }
                      label="Régimen general"
                      sx={{ color: dashboardTheme.text_color }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={0.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!empresa.exportador}
                          onChange={handleCheck}
                          name="exportador"
                          disabled={disabled}
                          sx={checkboxSx}
                        />
                      }
                      label="Exportador"
                      sx={{ color: dashboardTheme.text_color }}
                    />

                    {empresa.exportador && (
                      <RadioGroup
                        row={!isMobile}
                        name="exportador_tipo"
                        value={empresa.exportador_tipo || "NO_HABITUAL"}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="NO_HABITUAL"
                          control={<Radio disabled={disabled} sx={checkboxSx} />}
                          label="No habitual"
                          sx={{ color: dashboardTheme.text_color }}
                        />

                        <FormControlLabel
                          value="HABITUAL"
                          control={<Radio disabled={disabled} sx={checkboxSx} />}
                          label="Habitual"
                          sx={{ color: dashboardTheme.text_color }}
                        />
                      </RadioGroup>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={0.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!empresa.contribuyente_rimpe}
                          onChange={handleCheck}
                          name="contribuyente_rimpe"
                          disabled={disabled}
                          sx={checkboxSx}
                        />
                      }
                      label="Contribuyente Régimen RIMPE"
                      sx={{ color: dashboardTheme.text_color }}
                    />

                    {empresa.contribuyente_rimpe && (
                      <FormControl fullWidth size="small" sx={fieldSx}>
                        <InputLabel>Tipo RIMPE</InputLabel>
                        <Select
                          label="Tipo RIMPE"
                          name="regimen_tipo"
                          value={empresa.regimen_tipo || empresa.rimpe_tipo || ""}
                          onChange={handleChange}
                          disabled={disabled}
                        >
                          <MenuItem value="EMPRENDEDOR">
                            Contribuyente Régimen RIMPE
                          </MenuItem>
                          <MenuItem value="NEGOCIO_POPULAR">
                            Contribuyente Negocio Popular - Régimen RIMPE
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <Stack spacing={1}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: dashboardTheme.text_color,
                  }}
                >
                  Logo de la Empresa
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    variant="contained"
                    onClick={() => logoRef.current?.click()}
                    startIcon={<FolderOpenRoundedIcon />}
                    disabled={disabled}
                    sx={primaryButtonSx}
                  >
                    Seleccionar
                  </Button>

                  <input
                    ref={logoRef}
                    type="file"
                    name="logo_empresa"
                    accept="image/png,image/jpeg"
                    hidden
                    onChange={handleFile}
                  />

                  {!!empresa.logo_empresa && (
                    <Button
                      variant="outlined"
                      onClick={() => openPreview(empresa.logo_empresa)}
                      startIcon={<SearchRoundedIcon />}
                      sx={secondaryButtonSx}
                    >
                      Ver
                    </Button>
                  )}

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: dashboardTheme.muted_text_color,
                      wordBreak: "break-word",
                    }}
                  >
                    {fileLabel(empresa.logo_empresa)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </>
        )}
      </Stack>

      {!loading && canSave && (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 2,
            borderTop: `1px solid ${dashboardTheme.border_color}`,
            display: "flex",
            justifyContent: "flex-end",
            bgcolor: dashboardTheme.card_background,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} sx={{ color: dashboardTheme.button_text_color }} />
              ) : (
                <SaveRoundedIcon />
              )
            }
            disabled={saving}
            sx={{
              ...primaryButtonSx,
              height: 40,
              px: 2.4,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {saving ? "Guardando..." : empresa.id ? "Guardar Cambios" : "Crear Empresa"}
          </Button>
        </Box>
      )}

      <Modal open={estModalOpen} onClose={closeEstModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: dashboardTheme.card_background,
            p: { xs: 2, sm: 3 },
            borderRadius: "16px",
            width: "min(760px, calc(100vw - 32px))",
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow:
              dashboardTheme.theme_mode === "dark"
                ? "0 24px 60px rgba(0,0,0,0.45)"
                : "0 24px 60px rgba(17,24,39,0.18)",
            outline: "none",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 18,
              mb: 2,
              color: dashboardTheme.text_color,
            }}
          >
            {estEditIndex !== null ? "Editar establecimiento" : "Añadir establecimiento"}
          </Typography>

          {estError && (
            <Paper
              elevation={0}
              sx={{
                p: 1.2,
                mb: 2,
                bgcolor:
                  dashboardTheme.theme_mode === "dark"
                    ? "rgba(239,68,68,0.12)"
                    : "#fef2f2",
                border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: "12px",
              }}
            >
              <Typography sx={{ color: "#ef4444", fontWeight: 600, fontSize: 13 }}>
                {estError}
              </Typography>
            </Paper>
          )}

          <Grid container spacing={1.6}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Código"
                value={estForm.codigo}
                placeholder="001"
                onChange={(e) =>
                  handleEstFormChange({
                    codigo: e.target.value.replace(/\D/g, "").slice(0, 3),
                  })
                }
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Nombre"
                value={estForm.nombre}
                placeholder="MATRIZ"
                onChange={(e) =>
                  handleEstFormChange({
                    nombre: e.target.value,
                  })
                }
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Dirección"
                value={estForm.direccion}
                onChange={(e) =>
                  handleEstFormChange({
                    direccion: e.target.value,
                  })
                }
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!estForm.activo}
                    onChange={(e) =>
                      handleEstFormChange({
                        activo: e.target.checked,
                      })
                    }
                    sx={checkboxSx}
                  />
                }
                label="Establecimiento activo"
                sx={{ color: dashboardTheme.text_color }}
              />
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            justifyContent="flex-end"
            sx={{ mt: 2.5 }}
          >
            <Button onClick={closeEstModal} variant="outlined" sx={secondaryButtonSx}>
              Cancelar
            </Button>

            <Button variant="contained" onClick={saveEstablecimiento} sx={primaryButtonSx}>
              Guardar
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default EnterpriseForm;